if (!customElements.get("video-reels")) {
  customElements.define(
    "video-reels",
    class VideoReels extends HTMLElement {
      constructor() {
        super();
        this.activeClass = "active";
        this.swiper = this.querySelector(".wt-slider__container");
      }

      connectedCallback() {
        this._sectionObserver = null;
        this._isVisible = false;
        this._dragging = false;
        this._isSequence = this.dataset.videoSequence === "true";
        this._settledSelection = this.dataset.videoSelection === "settled";
        this._posterFrames = new Map();
        this._onVideoPlay = (event) => {
          const active = this.findActiveSlide(this._swiperInstance);
          if (!this._isVisible || this._dragging || document.hidden ||
              (this._settledSelection && this._swiperInstance?.animating) || !active?.contains(event.target)) {
            event.target.pause();
          }
        };
        this._onVideoPlaying = (event) => {
          if (event.target.seeking || this.seekVideoStart(event.target)) return;
          this.revealVideoFrame(event.target);
        };
        this._onVideoMetadata = (event) => {
          this.seekVideoStart(event.target);
          this.updateVideoProgress(event.target);
        };
        this._onVideoProgress = (event) => this.updateVideoProgress(event.target);
        this._onVideoInput = (event) => {
          if (event.target.matches('.video-reels__seek')) this.seekVideo(event.target);
        };
        this._onVideoReset = (event) => {
          this.cancelPosterFrame(event.target);
          // Keep the decoded frame visible while scrubbing, not the opening poster.
          if (event.type === 'seeking' && event.target.closest('[data-swiper-slide]')?.querySelector('.video-reels__seek')) return;
          event.target.closest('.sonic-reel-media')?.classList.remove('is-reel-video-ready');
          this.updateVideoProgress(event.target);
        };
        this._onVideoSeeked = (event) => {
          if (!event.target.paused) this.revealVideoFrame(event.target);
        };
        this._onVisibilityChange = () => {
          if (document.hidden) this.pauseAllVideos();
          else if (this._swiperInstance) this.playVideoInActiveSlide(this._swiperInstance);
        };
        this._onSoundToggle = (event) => {
          const button = event.target.closest(".wt-video__sound-toggle");
          if (button && this._swiperInstance) this.toggleSound(this._swiperInstance, button);
        };
        this._onVideoEnded = (event) => {
          const swiper = this._swiperInstance;
          if (!this._isSequence && event.target.dataset.videoTrimLoop === "true") {
            event.target.currentTime = this.getVideoStart(event.target);
            if (swiper && !swiper.destroyed) this.playVideoInActiveSlide(swiper);
            return;
          }
          if (!this._isSequence || !swiper || swiper.destroyed ||
              !this._isVisible || document.hidden || this._dragging || swiper.animating) return;
          const active = this.findActiveSlide(swiper);
          if (active?.querySelector("video") !== event.target || !event.target.ended) return;
          const nextIndex = swiper.slides.indexOf(active) + 1;
          if (nextIndex > 0 && nextIndex < swiper.slides.length) swiper.slideTo(nextIndex);
        };
        this.addEventListener("play", this._onVideoPlay, true);
        this.addEventListener("playing", this._onVideoPlaying, true);
        this.addEventListener("loadedmetadata", this._onVideoMetadata, true);
        this.addEventListener("durationchange", this._onVideoProgress, true);
        this.addEventListener("timeupdate", this._onVideoProgress, true);
        this.addEventListener("input", this._onVideoInput);
        this.addEventListener("error", this._onVideoReset, true);
        this.addEventListener("emptied", this._onVideoReset, true);
        this.addEventListener("seeking", this._onVideoReset, true);
        this.addEventListener("seeked", this._onVideoSeeked, true);
        this.addEventListener("ended", this._onVideoEnded, true);
        this.addEventListener("click", this._onSoundToggle);
        document.addEventListener("visibilitychange", this._onVisibilityChange);
        this.init();
      }

      disconnectedCallback() {
        if (this._sectionObserver) {
          this._sectionObserver.disconnect();
          this._sectionObserver = null;
        }
        if (this.checkInterval) clearInterval(this.checkInterval);
        cancelAnimationFrame(this._selectionFrame);
        this._swiperHandlers?.forEach(([event, handler]) => {
          this._swiperInstance?.off(event, handler);
        });
        this.removeEventListener("play", this._onVideoPlay, true);
        this.removeEventListener("playing", this._onVideoPlaying, true);
        this.removeEventListener("loadedmetadata", this._onVideoMetadata, true);
        this.removeEventListener("durationchange", this._onVideoProgress, true);
        this.removeEventListener("timeupdate", this._onVideoProgress, true);
        this.removeEventListener("input", this._onVideoInput);
        this.removeEventListener("error", this._onVideoReset, true);
        this.removeEventListener("emptied", this._onVideoReset, true);
        this.removeEventListener("seeking", this._onVideoReset, true);
        this.removeEventListener("seeked", this._onVideoSeeked, true);
        this.removeEventListener("ended", this._onVideoEnded, true);
        this.removeEventListener("click", this._onSoundToggle);
        document.removeEventListener("visibilitychange", this._onVisibilityChange);
        this.pauseAllVideos();
        this._posterFrames.forEach((_job, video) => this.cancelPosterFrame(video));
        this._swiperInstance = null;
      }

      cancelPosterFrame(video) {
        const job = this._posterFrames.get(video);
        if (!job) return;
        this._posterFrames.delete(video);
        if (job.videoFrame) video.cancelVideoFrameCallback?.(job.id);
        else cancelAnimationFrame(job.id);
      }

      revealVideoFrame(video) {
        const media = video.closest('.sonic-reel-media');
        if (!media || media.classList.contains('is-reel-video-ready') || this._posterFrames.has(video)) return;
        const job = { videoFrame: typeof video.requestVideoFrameCallback === 'function', id: null };
        this._posterFrames.set(video, job);
        const reveal = (_now, frame) => {
          if (this._posterFrames.get(video) !== job) return;
          this._posterFrames.delete(video);
          const active = this.findActiveSlide(this._swiperInstance);
          if (!this.isConnected || !this._isVisible || this._dragging || document.hidden ||
              video.paused || video.seeking || video.readyState < 2 || !active?.contains(video)) return;
          // A decoded frame before the configured trim point must stay covered.
          if ((frame?.mediaTime ?? video.currentTime) + 0.001 < this.getVideoStart(video)) {
            this.revealVideoFrame(video);
            return;
          }
          media.classList.add('is-reel-video-ready');
        };
        // `playing` is not a compositor guarantee. Older browsers get two paint turns.
        if (job.videoFrame) job.id = video.requestVideoFrameCallback(reveal);
        else job.id = requestAnimationFrame(() => {
          if (this._posterFrames.get(video) === job) job.id = requestAnimationFrame(reveal);
        });
      }

      observeSection() {
        const observerOptions = {
          root: null,
          rootMargin: "0px",
          threshold: 0.1,
        };

        const sectionObserver = new IntersectionObserver(
          (entries, observer) => {
            entries.forEach((entry) => {
              this._isVisible = entry.isIntersecting;
              if (this._isVisible) this.playVideoInActiveSlide(this._swiperInstance);
              else this.pauseAllVideos();
            });
          },
          observerOptions,
        );

        sectionObserver.observe(this);
        this._sectionObserver = sectionObserver;
      }

      pauseAllVideos() {
        this.querySelectorAll("video").forEach((video) => {
          this.cancelPosterFrame(video);
          video.pause();
        });
      }

      hardStopAllExceptActive(swiper) {
        const active = this.getSelectedSlide(swiper);
        swiper.slides.forEach((slide) => {
          const video = slide.querySelector("video");
          if (!video) return;

          //Stop and mute all except active
          if (slide !== active) {
            this.cancelPosterFrame(video);
            video.pause();
            video.muted = true;
            video.removeAttribute("autoplay"); // avoid Safari storms
            video.autoplay = false;
          }
        });
      }

      handleSoundToggle(swiper) {
        const soundOn = swiper.el.dataset.sound === "on";
        swiper.slides.forEach((slide) => {
          const button = slide.querySelector(".wt-video__sound-toggle");
          if (!button) return;
          button.dataset.sound = soundOn ? "on" : "off";
          button.setAttribute("aria-pressed", soundOn ? "true" : "false");
        });
      }

      toggleSound(swiper, button) {
        const activeSlide = this.findActiveSlide(swiper);
        if (button.closest("[data-swiper-slide]") !== activeSlide ||
            this._dragging || swiper.animating || swiper.allowClick === false) return;
        const video = activeSlide?.querySelector("video");
        if (!video) return;

        this.hardStopAllExceptActive(swiper);
        swiper.el.dataset.sound = swiper.el.dataset.sound === "on" ? "off" : "on";
        // Playback stays in the click stack so Safari permits unmuting.
        this.playVideoInActiveSlide(swiper);
      }

      sanitizeVideosOnce() {
        if (this._sanitized) return;
        this._sanitized = true;
        const preloadActiveOnly = this.dataset.videoPreload === "active";
        const activeVideo = this.querySelector(".swiper-slide-active video");

        this.querySelectorAll("video").forEach((video) => {
          video.pause();
          video.muted = true;
          video.removeAttribute("autoplay");
          video.autoplay = false;
          if (!this._isSequence && Number(video.dataset.videoStart) > 0) {
            video.dataset.videoTrimLoop = video.loop ? "true" : "false";
            video.loop = false;
            video.removeAttribute("loop");
          } else if (this._isSequence) {
            video.loop = false;
            video.removeAttribute("loop");
          }
          video.setAttribute("playsinline", "");
          video.setAttribute("webkit-playsinline", "");
          video.preload = preloadActiveOnly && video !== activeVideo ? "none" : "metadata";
          this.seekVideoStart(video);
        });
      }

      getVideoStart(video) {
        const start = Number(video.dataset.videoStart);
        if (!Number.isFinite(start) || start <= 0 ||
            (Number.isFinite(video.duration) && start >= video.duration)) return 0;
        return start;
      }

      seekVideoStart(video) {
        const start = this.getVideoStart(video);
        if (!start || video.readyState < 1 || video.currentTime >= start) return false;
        video.currentTime = start;
        return true;
      }

      updateVideoProgress(video) {
        const slide = video.closest('[data-swiper-slide]');
        const slider = slide?.querySelector('.video-reels__seek');
        if (!slider) return;
        const duration = video.duration - this.getVideoStart(video);
        const available = Number.isFinite(duration) && duration > 0 && !video.error && video.readyState >= 1;
        slider.disabled = !available || slide !== this.findActiveSlide(this._swiperInstance);
        const elapsed = available ? Math.min(duration, Math.max(0, video.currentTime - this.getVideoStart(video))) : 0;
        slider.max = available ? duration : 0;
        slider.value = elapsed;
        slider.style.setProperty('--reel-progress', `${available ? elapsed / duration * 100 : 0}%`);
        const clock = seconds => `${Math.floor(seconds / 60)}:${String(Math.floor(seconds % 60)).padStart(2, '0')}`;
        slider.setAttribute('aria-valuetext', `${clock(elapsed)} / ${clock(available ? duration : 0)}`);
      }

      seekVideo(slider) {
        const swiper = this._swiperInstance;
        const slide = slider.closest('[data-swiper-slide]');
        const video = slide?.querySelector('video');
        if (!video || slider.disabled || !swiper || swiper.destroyed || swiper.animating ||
            this._dragging || slide !== this.findActiveSlide(swiper)) return;
        const value = Number(slider.value);
        if (!Number.isFinite(value)) return;
        video.currentTime = Math.min(video.duration, Math.max(this.getVideoStart(video), this.getVideoStart(video) + value));
        this.updateVideoProgress(video);
      }

      playVideoInActiveSlide(swiper) {
        if (!swiper || !this._isVisible || document.hidden || this._dragging) return;
        if (this._settledSelection && swiper.animating) return;
        const sound = swiper.el.dataset.sound;
        const activeSlideVideo =
          this.findActiveSlide(swiper)?.querySelector("video");
        if (activeSlideVideo) {
          // Completed steps stay on their last frame until explicitly replayed.
          if (this._isSequence && activeSlideVideo.ended) return;
          this.seekVideoStart(activeSlideVideo);
          activeSlideVideo.preload = "metadata";
          activeSlideVideo.muted = sound !== "on"; // if sound !== "on", keep it muted
          activeSlideVideo.defaultMuted = sound !== "on";
          if (sound === "on") {
            activeSlideVideo.removeAttribute("muted");
          } else {
            activeSlideVideo.setAttribute("muted", "");
          }
          this.handleSoundToggle(swiper);

          // Do not queue playback for an intermediate slide during a swipe.
          activeSlideVideo.play().catch(() => {});
        }
      }

      findActiveSlide(swiper) {
        if (!swiper) return null;
        return this._settledSelection
          ? this.querySelector("[data-swiper-slide].active")
          : this.getSelectedSlide(swiper);
      }

      getSelectedSlide(swiper) {
        // A bounded track can reach the final card without changing activeIndex.
        if (this._isSequence && swiper.isEnd && !swiper.isBeginning && !swiper.params?.loop) {
          return swiper.slides[swiper.slides.length - 1];
        }
        return swiper.slides[swiper.activeIndex];
      }

      toggleActiveClass(swiper) {
        const activeSlide = this.getSelectedSlide(swiper);
        swiper.slides.forEach((slide) => {
          slide.classList.remove(this.activeClass);

          if (activeSlide === slide) {
            slide.classList.add(this.activeClass);
          }
          const video = slide.querySelector('video');
          if (video) this.updateVideoProgress(video);
        });
      }

      handleSlideChange(swiper) {
        if (this._settledSelection && (this._dragging || (this._isSequence && swiper.animating))) return;
        this.hardStopAllExceptActive(swiper);
        this.toggleActiveClass(swiper);
        this.playVideoInActiveSlide(swiper);
      }

      queueSettledSelection(swiper) {
        cancelAnimationFrame(this._selectionFrame);
        // Swiper emits slideChange/touchEnd before it finishes updating its state.
        this._selectionFrame = requestAnimationFrame(() => {
          if (this.isConnected && !swiper.destroyed) this.handleSlideChange(swiper);
        });
      }

      addVideoEventHandlers(swiper) {
        this._swiperInstance = swiper;
        this._swiperHandlers = [];
        const on = (event, handler) => {
          this._swiperHandlers.push([event, handler]);
          swiper.on(event, handler);
        };
        on("slideChange", () => {
          if (this._settledSelection) this.queueSettledSelection(swiper);
          else this.handleSlideChange(swiper);
        });
        if (this._settledSelection) {
          on("transitionStart", () => {
            if (!this._isSequence) {
              this.pauseAllVideos();
              this.queueSettledSelection(swiper);
            }
          });
          on("touchStart", () => { this._dragging = true; });
          on("sliderFirstMove", () => this.pauseAllVideos());
          on("touchEnd", () => {
            this._dragging = false;
            this.queueSettledSelection(swiper);
          });
          on("transitionEnd", () => this.queueSettledSelection(swiper));
          if (this._isSequence) {
            on("reachEnd", () => this.queueSettledSelection(swiper));
            on("fromEdge", () => this.queueSettledSelection(swiper));
          }
          on("loopFix", () => this.queueSettledSelection(swiper));
          on("resize", () => this.queueSettledSelection(swiper));
        }
        on("slidesLengthChange", () => this.handleSoundToggle(swiper));
        on("update", () => {
          this.handleSoundToggle(swiper);
          if (this._settledSelection) this.queueSettledSelection(swiper);
        });
      }

      checkSwiperInitialization() {
        const swiperContainer = this.swiper;
        const mySwiperInstance = swiperContainer?.swiper;

        if (
          swiperContainer?.classList.contains("swiper-initialized") &&
          mySwiperInstance
        ) {
          if (!this.swiper?.dataset.sound) {
            this.swiper.dataset.sound = "off";
          }
          this.sanitizeVideosOnce();
          this.addVideoEventHandlers(mySwiperInstance);
          this.handleSlideChange(mySwiperInstance);
          this.handleSoundToggle(mySwiperInstance);
          this.observeSection();
          clearInterval(this.checkInterval);
        }
      }

      init() {
        this.checkInterval = setInterval(
          this.checkSwiperInitialization.bind(this),
          250,
        );
      }
    },
  );
}
